/**
 * The two blocks the shell draws straight off ledger facts.
 *
 * CHARTER §7.130 gives the test for what belongs here rather than inside a
 * plugin's own iframe: a rendering rule that can be one function every plugin
 * shares. A conversation across sessions and a tree of delegated work are both
 * of those — the facts are `inbound`, a channel's `reply` call, `tool_call` /
 * `tool_result`, `child_spawned` and `turn_final`, and every plugin that wanted
 * to show them would otherwise write the same reader again.
 *
 * Both take a `tail` source, so their rows are ledger events, a shape this
 * shell already knows. That is why neither block has a field pointing at
 * anything: there is nothing for a plugin to name. What it *does* choose is the
 * filter — which kinds, whose session, how many.
 */

import { useMemo } from "react";
import type { PanelBlock } from "./spec";
import { formatTime, toneForState, TONE_GLYPH } from "../lib/format";
import { Clip, EmptyState } from "../shell/ui";

/** A `<channel>.reply` call is the words the agent said (§7.139 ①). */
function isReply(tool: string): boolean {
  return tool.endsWith(".reply");
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** Who a line belongs to. Three, because the ledger only tells apart three. */
export type Voice = "person" | "agent" | "machine";

export interface ThreadLine {
  key: string;
  /** The tail cursor as a number, or the row's place in the answer when the
   *  cursor is not one this shell can read. Cursors are opaque in the
   *  contract, so the fallback is the order the server sent them in. */
  order: number;
  session: string;
  ts: unknown;
  voice: Voice;
  /** What to call the speaker, in the shell's own words. */
  who: string;
  body: string;
  /** A tool line's own tone, so a failure does not read like a success. */
  tone: string;
}

/**
 * One ledger event as one line of a conversation, or nothing.
 *
 * `origin` is the submitter's own statement about who is talking (§7.127), and
 * it is the only thing that separates a person from a plugin speaking for the
 * machine. A kind this does not recognise is dropped rather than summarised:
 * a thread that showed `l0.usage` as a line of dialogue would be inventing
 * speech.
 */
export function threadLine(
  event: Record<string, unknown>,
  index = 0,
): ThreadLine | null {
  const kind = text(event.kind);
  const row = asRecord(event.row);
  const session = text(event.session_id);
  const cursor = Number(event.cursor);
  const key = String(event.cursor ?? "") || text(row.id) || String(index);
  const order = Number.isFinite(cursor) ? cursor : index;
  const base = { key, order, session, ts: event.ts, tone: "neutral" };
  if (kind === "l0.inbound") {
    const human = text(row.origin).toUpperCase() !== "SYSTEM";
    return {
      ...base,
      voice: human ? "person" : "machine",
      who: human ? "人" : "系统",
      body: text(row.text),
    };
  }
  if (kind === "l0.tool_call") {
    const call = asRecord(row.call);
    const tool = text(call.tool) || text(row.tool);
    if (isReply(tool)) {
      return {
        ...base,
        voice: "agent",
        who: "它",
        body: text(asRecord(call.arguments).text),
      };
    }
    return { ...base, voice: "machine", who: "调用", body: tool };
  }
  if (kind === "l0.tool_result") {
    const result = asRecord(row.result);
    const tool = text(result.tool) || text(row.tool);
    if (isReply(tool)) return null; // the words are on the call; a receipt is not speech
    const ok = result.ok !== false;
    return {
      ...base,
      voice: "machine",
      who: "结果",
      body: ok ? tool : `${tool}：${text(result.error) || "失败"}`,
      tone: ok ? "good" : "critical",
    };
  }
  if (kind === "l0.child_spawned") {
    const spec = asRecord(row.spec);
    return {
      ...base,
      voice: "machine",
      who: "派下去",
      body: text(spec.label) || text(spec.purpose) || text(row.session_id),
    };
  }
  if (kind === "l0.turn_final") {
    const result = asRecord(row.result);
    const state = text(result.state) || text(row.state);
    return {
      ...base,
      voice: "machine",
      who: "一轮结束",
      body: state,
      tone: toneForState(state),
    };
  }
  return null;
}

/**
 * A conversation, across as many sessions as the filter let through.
 *
 * Oldest first inside a session, because that is the order the words were
 * said; sessions in the order their first line appears, because a thread is
 * read from the top. A session gets a heading whenever there is more than one
 * — a single-session thread with a heading saying which session is a label
 * over a thing with no alternative.
 */
export function ThreadLines({
  block,
  rows,
}: {
  block: PanelBlock;
  rows: Record<string, unknown>[];
}) {
  const groups = useMemo(() => {
    const lines = rows
      .map((row, index) => threadLine(row, index))
      .filter((line): line is ThreadLine => line !== null)
      .sort((a, b) => a.order - b.order);
    const bySession = new Map<string, ThreadLine[]>();
    for (const line of lines) {
      const list = bySession.get(line.session);
      if (list) list.push(line);
      else bySession.set(line.session, [line]);
    }
    return [...bySession.entries()];
  }, [rows]);

  if (!groups.length) {
    return (
      <EmptyState
        title="这里现在是空的"
        hint={block.empty_text || "还没有能读成一句话的账本行。"}
      />
    );
  }
  return (
    <div className="thread">
      {groups.map(([session, lines]) => (
        <section className="thread-session" key={session}>
          {groups.length > 1 ? (
            <h4 className="thread-session-head micro">
              <span className="mono">{session || "（没有会话号）"}</span>
              <span className="faint">{lines.length} 行</span>
            </h4>
          ) : null}
          <ol className="thread-lines">
            {lines.map((line) => (
              <li key={line.key} className="thread-line" data-voice={line.voice}>
                <span className="thread-who">{line.who}</span>
                <time className="thread-time mono">{formatTime(line.ts)}</time>
                <span className="thread-body">
                  {line.tone !== "neutral" ? (
                    <span className="thread-mark" aria-hidden="true">
                      {TONE_GLYPH[line.tone as keyof typeof TONE_GLYPH] ?? ""}
                    </span>
                  ) : null}
                  {line.body ? <Clip text={line.body} /> : <span className="faint">（没有字）</span>}
                </span>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

export interface GraphNode {
  id: string;
  label: string;
  parent: string | null;
  ts: unknown;
  /** What became of the turn that ran in this session, when the tail says. */
  state: string;
  children: GraphNode[];
}

/**
 * The forest a set of `child_spawned` rows describes.
 *
 * The edge is not in the payload: the core writes the row *in the parent's
 * session* and puts the child's session in the body (`pa/core/loop/children.py`),
 * so the event's own `session_id` is the parent. A root is any session that
 * spawned something without itself having been spawned inside this window —
 * which is what "as far back as the filter let us see" honestly looks like.
 *
 * Except when there was no parent to write down. `spawn_child` falls back to
 * `session_id=parent_session_id or child_session`, so a spawner that passed no
 * parent session (a plugin asking the model a question of its own —
 * `memory_timeline`'s episode turns are all like this) leaves a row whose
 * session *is* the child. That is an edge from a node to itself; read
 * literally it makes the node its own parent, and a node whose parent is
 * present is never a root, so the whole forest comes out empty. It is read
 * here as what it is: a spawn whose parent nobody recorded.
 *
 * `turn_final` rows are folded in when the filter let them through, so a node
 * can say how its work ended. Nothing else is read: a task graph drawn from
 * anything but the facts would be a diagram of what someone hoped happened.
 */
export function buildGraph(rows: Record<string, unknown>[]): GraphNode[] {
  const nodes = new Map<string, GraphNode>();
  const node = (id: string): GraphNode => {
    let found = nodes.get(id);
    if (!found) {
      found = { id, label: "", parent: null, ts: null, state: "", children: [] };
      nodes.set(id, found);
    }
    return found;
  };
  const states = new Map<string, string>();
  for (const event of rows) {
    const kind = text(event.kind);
    const row = asRecord(event.row);
    if (kind === "l0.turn_final") {
      const result = asRecord(row.result);
      states.set(text(event.session_id), text(result.state) || text(row.state));
      continue;
    }
    if (kind !== "l0.child_spawned") continue;
    const parent = text(event.session_id);
    const child = text(row.session_id);
    if (!child) continue;
    const spec = asRecord(row.spec);
    const it = node(child);
    it.parent = parent && parent !== child ? parent : null;
    it.ts = event.ts;
    it.label = text(spec.label) || text(spec.purpose) || text(spec.task) || child;
    if (it.parent) node(it.parent);
  }
  for (const it of nodes.values()) {
    it.state = states.get(it.id) ?? "";
    if (!it.label) it.label = it.id;
  }
  for (const it of nodes.values()) {
    const parent = it.parent ? nodes.get(it.parent) : undefined;
    if (parent && parent !== it) parent.children.push(it);
  }
  for (const it of nodes.values()) {
    it.children.sort((a, b) => Number(a.ts ?? 0) - Number(b.ts ?? 0));
  }
  return [...nodes.values()].filter((it) => !it.parent || !nodes.has(it.parent));
}

function GraphBranch({ node, depth }: { node: GraphNode; depth: number }) {
  return (
    <li className="graph-node" style={{ marginInlineStart: depth ? "18px" : 0 }}>
      <div className="graph-row">
        <span className="graph-mark" aria-hidden="true">
          {depth ? "└" : "●"}
        </span>
        <span className="graph-label">
          <Clip text={node.label} />
        </span>
        {node.state ? (
          <span className="graph-state micro" data-tone={toneForState(node.state)}>
            {TONE_GLYPH[toneForState(node.state)]} {node.state}
          </span>
        ) : null}
        <span className="graph-id mono micro">{node.id}</span>
      </div>
      {node.children.length ? (
        <ul className="graph-branch">
          {node.children.map((child) => (
            <GraphBranch key={child.id} node={child} depth={depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * Who delegated what to whom, as the ledger has it.
 *
 * Drawn as a nested list and not as a canvas: the edges are a forest (a child
 * session has exactly one parent row), the shell has no layout engine, and a
 * list is the one rendering that is still readable at 220px and to a screen
 * reader. A plugin that wants a drawn graph has `surface` for that.
 */
export function GraphTree({
  block,
  rows,
}: {
  block: PanelBlock;
  rows: Record<string, unknown>[];
}) {
  const roots = useMemo(() => buildGraph(rows), [rows]);
  if (!roots.length) {
    return (
      <EmptyState
        title="这里现在是空的"
        hint={block.empty_text || "还没有派下去过任何活。"}
      />
    );
  }
  return (
    <ul className="graph">
      {roots.map((root) => (
        <GraphBranch key={root.id} node={root} depth={0} />
      ))}
    </ul>
  );
}